import * as React from "react";
import styled from "styled-components";
import {
  ConstBorderRound,
  ConstBreakPoint,
  ConstColor,
  ConstShadow,
} from "../../../../config/styles_const/styles_const";
import { Contract } from "../../../../domain/model/contract";
import { Customer, Status } from "../../../../domain/model/customer";
import { Entry } from "../../../../domain/model/entry";
import { Project } from "../../../../domain/model/project";
import { entity } from "../../../../infra/api/rpc/api";
import AvatarMiddle from "../../../common/atom/avatar/avatar_middle";
import BtnLinkCorporateSmall from "../../../common/atom/btn/btn_link_corporate_small";
import HeadlineMiddle from "../../../common/atom/text/headline_middle";
import Paragraph from "../../../common/atom/text/paragraph";
import ParagraphLink from "../../../common/atom/text/paragraph_link";
import ParagraphSup from "../../../common/atom/text/paragraph_sup";

interface IProps {
  entries: Entry[];
  customers: Customer[];
  contracts: Contract[];
  project: Project | null;
}

export const ClientEntriesMain: React.FC<IProps> = (props) => {
  const { entries, customers, contracts, project } = props;

  if (!entries || !customers || !contracts || !project) {
    return <div />;
  }

  const projectThumbnailURL: string = project.getThumbnailURL();
  const entriesCount: number = entries.length;
  const contractsCount: number = contracts.length;
  const companyID: string = project.companyID;

  const companyId: string = project.companyID;
  // Customerの連想配列を作成し、Entryから連携させる。
  const hashCustomer: { [customerId: string]: Customer } = {};
  for (const item of customers) {
    hashCustomer[item.id] = item;
  }

  // Contractの連想配列を作成し、Entryから連携させる。
  const hashContract: { [customerId: string]: Contract } = {};
  if (contracts) {
    for (const item of contracts) {
      hashContract[item.customerID] = item;
    }
  }

  return (
    <div>
      <ProjectThumbnailWrapper>
        <ProjectThumbnail src={projectThumbnailURL} />
      </ProjectThumbnailWrapper>
      <EntriesInfoWrapper>
        <EntriesInfoSubTitle>エントリー数</EntriesInfoSubTitle>
        <EntriesInfoSubBody bold={true}>{entriesCount}</EntriesInfoSubBody>
        <EntriesInfoSubTitle>成約済</EntriesInfoSubTitle>
        <EntriesInfoSubBody bold={true}>{contractsCount}</EntriesInfoSubBody>
      </EntriesInfoWrapper>

      <SubTitleWrapper>
        <HeadlineMiddle>エントリー者一覧</HeadlineMiddle>
      </SubTitleWrapper>

      {entries.map((entry: Entry, index) => {
        const customer: Customer = hashCustomer[entry.customerID];
        const contract: Contract = hashContract[entry.customerID];
        const isDeleteCustomer: boolean =
          customer.status === Status.Status_Delete;
        return (
          <Wrapper key={index}>
            <Area area={"name"}>
              <CustomerArea>
                <CustomerAvatar src={customer.getIconURL()} />
                <TextLink
                  to={`/client/scout/customer/${entry.customerID}`}
                  bold={true}
                >
                  {customer.catchphrase.substring(0, 20)}
                </TextLink>
              </CustomerArea>
            </Area>

            <Area area={"contractStatus"}>
              {!isDeleteCustomer &&
                contract &&
                contract.requester ===
                  entity.Contract.Requester.Requester_Admin && (
                  <ClosedContractWrapper>
                    <Paragraph bold={true}>Adminから報告済</Paragraph>
                  </ClosedContractWrapper>
                )}
              {!isDeleteCustomer &&
                contract &&
                contract.requester ===
                  entity.Contract.Requester.Requester_Company && (
                  <ClosedContractWrapper>
                    <Paragraph bold={true}>成約報告済み</Paragraph>
                  </ClosedContractWrapper>
                )}
              {!isDeleteCustomer &&
                contract &&
                contract.requester ===
                  entity.Contract.Requester.Requester_Customer &&
                contract.isApproved && (
                  <ClosedContractWrapper>
                    <Paragraph bold={true}>成約報告の承認済み</Paragraph>
                  </ClosedContractWrapper>
                )}
              {!isDeleteCustomer &&
                contract &&
                contract.requester ===
                  entity.Contract.Requester.Requester_Customer &&
                !contract.isApproved && (
                  <ApproveContractWrapper>
                    <Paragraph bold={true}>
                      成約報告の承認依頼が届いています
                    </Paragraph>
                  </ApproveContractWrapper>
                )}
            </Area>

            <Area area={"contractLink"}>
              {!isDeleteCustomer && !contract && (
                <BtnLinkCorporateSmall
                  to={`/client/contract/registration/${companyId}/${entry.projectID}/${entry.customerID}`}
                >
                  成約報告する
                </BtnLinkCorporateSmall>
              )}
              {!isDeleteCustomer &&
                contract &&
                contract.requester !==
                  entity.Contract.Requester.Requester_Admin && (
                  <BtnLinkCorporateSmall
                    to={`/client/contract/${companyId}/${entry.projectID}/${entry.customerID}`}
                  >
                    成約内容を確認する
                  </BtnLinkCorporateSmall>
                )}
            </Area>

            <Area area={"messageRoomLink"}>
              <BtnLinkCorporateSmall
                to={`/client/message/${entry.projectID}/${entry.customerID}/${companyID}`}
              >
                メッセージ画面へ
              </BtnLinkCorporateSmall>
            </Area>
          </Wrapper>
        );
      })}
    </div>
  );
};

export default ClientEntriesMain;

const CustomerArea = styled.div`
  display: flex;
`;

const templateSP = `
    " name            ...             contractStatus  contractStatus contractStatus    ...             ... "
    " ...             ...             ...             ...            ...               ...             ... "
    " contractLink    contractLink    contractLink    ...            messageRoomLink   messageRoomLink ... "
`;

const template = `
    "name ... contractStatus ... contractLink ... messageRoomLink "
`;

const Wrapper = styled.div`
  background: ${ConstColor.White};
  box-shadow: ${ConstShadow.BorderTopBottom};

  margin-bottom: -1px;
  padding: 10px 20px;

  display: grid;
  grid-template-areas: ${templateSP};
  grid-template-columns: auto 20px auto 15px auto auto 1fr;
  grid-template-rows: auto 10px auto;
  grid-column-gap: 0;
  grid-row-gap: 0;
  justify-content: center;

  @media screen and (min-width: ${ConstBreakPoint.Default}) {
    display: grid;
    grid-template-areas: ${template};
    grid-template-columns: auto 1fr auto 10px auto 10px auto;
    grid-template-rows: auto;
    grid-column-gap: 0;
    grid-row-gap: 0;
    justify-content: center;
  }
`;

const Area = styled.div<{ area: string }>`
  grid-area: ${(props) => props.area};
  display: grid;
  align-items: center;
`;

const CustomerAvatar = styled(AvatarMiddle)`
  width: 30px;
  height: 30px;
  margin-right: 10px;
`;

const ProjectThumbnailWrapper = styled.div`
  margin-bottom: 10px;
`;

const ProjectThumbnail = styled.img`
  width: 100%;
  height: 280px;
  object-fit: cover;
  border-radius: ${ConstBorderRound.CornerRoundAll};
`;

const EntriesInfoWrapper = styled.div`
  background: ${ConstColor.White};
  border: 1px solid ${ConstColor.PlaceholderAndBorder};
  border-radius: ${ConstBorderRound.CornerRoundAll};

  display: flex;
  align-items: center;
  padding: 20px;
  margin-bottom: 40px;
`;

const EntriesInfoSubTitle = styled(ParagraphSup)`
  margin-right: 10px;
`;

const EntriesInfoSubBody = styled(Paragraph)`
  margin-right: 40px;
`;

const SubTitleWrapper = styled.div`
  margin-bottom: 20px;
`;

const TextLink = styled(ParagraphLink)`
  color: ${ConstColor.Corporate};
`;

const ClosedContractWrapper = styled.div`
  & > p {
    color: ${ConstColor.Gray};
  }
`;

const ApproveContractWrapper = styled.div`
  & > p {
    color: #00d58e;
  }
`;



// WEBPACK FOOTER //
// ./src/presentation/component/client/entries/client_entries_main.tsx